import { gql } from '@apollo/client';
import { chakra, Tag } from '@chakra-ui/react';
import { t } from 'i18next';
import { useCallback, useMemo } from 'react';
import { ActionMeta, MultiValue } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { components, SelectOption } from '~components/ui/Select';
import { MaybePromise } from '~utils/types';
import {
  ConfigGroupsSelect_ConfigFragment,
  ConfigGroupsSelect_CustomerFragment,
  ConfigGroupsSelect_GroupFragment,
} from './__generated__/ConfigGroupSelect.graphql';

interface Props {
  tabIndex?: number;
  customer: ConfigGroupsSelect_CustomerFragment;
  config: ConfigGroupsSelect_ConfigFragment;
  value: ConfigGroupsSelect_GroupFragment[];
  isLoading: boolean;
  isDisabled?: boolean;
  onCreate: (customer: ConfigGroupsSelect_CustomerFragment, name: string) => MaybePromise<void>;
  onAdd: (
    config: ConfigGroupsSelect_ConfigFragment,
    group: ConfigGroupsSelect_GroupFragment,
  ) => MaybePromise<void>;
  onRemove: (
    config: ConfigGroupsSelect_ConfigFragment,
    group: ConfigGroupsSelect_GroupFragment,
  ) => MaybePromise<void>;
}

export function ConfigGroupsSelect({
  tabIndex,
  customer,
  config,
  value,
  isLoading,
  isDisabled = false,
  onCreate,
  onAdd,
  onRemove,
}: Props) {
  const availableGroups = useMemo(
    () =>
      (customer.groups || []).map<SelectOption>((group) => ({
        label: group.name,
        value: group.id,
      })),
    [customer],
  );

  const currentOption = useMemo(
    () => value.map<SelectOption>((v) => ({ label: v.name, value: v.id })),
    [value],
  );

  const handleChange = useCallback(
    (_value: MultiValue<SelectOption>, actionMeta: ActionMeta<SelectOption>) => {
      if (actionMeta.action === 'select-option' && actionMeta.option) {
        const group = customer.groups.find((g) => g.id === actionMeta.option?.value);
        if (!group) throw new Error('Selected group not available.');

        onAdd(config, group);
      } else if (actionMeta.action === 'remove-value') {
        const group = customer.groups.find((g) => g.id === actionMeta.removedValue.value);
        if (!group) throw new Error('Selected group not available.');

        onRemove(config, group);
      }
    },
    [customer, config, onAdd, onRemove],
  );

  const handleCreate = useCallback(
    (name: string) => {
      onCreate(customer, name);
    },
    [onCreate, customer],
  );

  const shouldDisable = isDisabled || isLoading;

  return (
    <CreatableSelect
      tabIndex={tabIndex}
      isMulti={true}
      components={components}
      isDisabled={shouldDisable}
      formatCreateLabel={(value) => (
        <>
          <chakra.span fontWeight="bold" fontSize="md">
            {t('create')}
          </chakra.span>
          <Tag
            colorScheme="gray"
            variant="outline"
            display="inline"
            marginLeft="2"
            verticalAlign="middle"
            fontSize="xs"
            paddingY="1"
            paddingX="2"
          >
            {value}
          </Tag>
        </>
      )}
      styles={{
        input: (base) => ({
          ...base,
          margin: 0,
          marginLeft: '0.5rem',
        }),
      }}
      isLoading={isLoading}
      onChange={handleChange}
      onCreateOption={handleCreate}
      options={availableGroups}
      value={currentOption}
      placeholder={t('selectCreateGroup')}
      isClearable={false}
      menuPlacement="auto"
    />
  );
}

ConfigGroupsSelect.graphql = {
  fragments: {
    ConfigGroupsSelect_customer: gql`
      fragment ConfigGroupsSelect_customer on Customer {
        id
        groups {
          id
          name
        }
      }
    `,
    ConfigGroupsSelect_config: gql`
      fragment ConfigGroupsSelect_config on ConfigurationModel {
        id
      }
    `,
    ConfigGroupsSelect_group: gql`
      fragment ConfigGroupsSelect_group on Group {
        id
        name
      }
    `,
  },
};
